import { useState } from 'react';
import {
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Timeline,
} from '@mantine/core';
import { IconAlertTriangle, IconPlayerPlay } from '@tabler/icons-react';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { CALL_ERROR_TYPE_LABELS, CallErrorDetail } from './call-error-detail';
import { callStatusColor, durationBetween, formatDuration, formatPhone, formatStamp, toMs } from './format';
import { useCallDetail } from './use-call-detail';

type Recording = {
  id: string;
  recordingId: string;
  format: string | null;
  recordingUrls: unknown;
  publicRecordingUrls: unknown;
  startedAt: string;
  endedAt: string | null;
};

function urlsHaveFormat(urls: unknown, format: 'mp3' | 'wav') {
  return !!urls && typeof urls === 'object' && typeof (urls as Record<string, unknown>)[format] === 'string';
}

function recordingFormat(recording: Recording) {
  if (recording.format === 'mp3' || recording.format === 'wav') return recording.format;
  if (urlsHaveFormat(recording.recordingUrls, 'mp3') || urlsHaveFormat(recording.publicRecordingUrls, 'mp3')) {
    return 'mp3';
  }
  if (urlsHaveFormat(recording.recordingUrls, 'wav') || urlsHaveFormat(recording.publicRecordingUrls, 'wav')) {
    return 'wav';
  }

  return null;
}

function collectedEntries(source: unknown) {
  if (!source || typeof source !== 'object') return [];

  return Object.entries(source as Record<string, unknown>)
    .filter(([key]) => !key.startsWith('_'))
    .map(([key, value]) => ({ key, value: typeof value === 'string' ? value : JSON.stringify(value) }));
}

function SectionLabel({ children }: { children: string }) {
  return (
    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
      {children}
    </Text>
  );
}

function TabCount({ count, color }: { count: number; color: string }) {
  if (count === 0) return null;

  return (
    <Badge size="xs" variant={color === 'red' ? 'filled' : 'light'} color={color} circle>
      {count}
    </Badge>
  );
}

export function CallDetailModal({
  organizationId,
  callControlId,
  onClose,
}: {
  organizationId: string;
  callControlId: string | null;
  onClose: () => void;
}) {
  if (!callControlId) return null;

  return (
    <Modal opened onClose={onClose} title="Call detail" size="xl">
      <CallDetail key={callControlId} organizationId={organizationId} callControlId={callControlId} />
    </Modal>
  );
}

function CallDetail({ organizationId, callControlId }: { organizationId: string; callControlId: string }) {
  const detail = useCallDetail(organizationId, callControlId);

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('flow');

  const playRecording = async (recordingId: string) => {
    setLoadingAudio(recordingId);
    setAudioError(null);
    setAudioUrl(null);

    const { data: link, error } = await api.recordings({ id: recordingId })['access-link'].get();

    setLoadingAudio(null);

    if (error) return setAudioError(getErrorMessage(error, 'Recording is not available.'));

    setAudioUrl(link.url);
  };

  const playVoicemail = async (voicemailId: string) => {
    setLoadingAudio(voicemailId);
    setAudioError(null);
    setAudioUrl(null);

    const { data: link, error } = await api.voicemails({ id: voicemailId })['access-link'].get();

    setLoadingAudio(null);

    if (error) return setAudioError(getErrorMessage(error, 'Voicemail is not available.'));

    setAudioUrl(link.url);
  };

  if (!detail) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const { log, session } = detail;

  if (!log && !session) {
    return (
      <Text c="dimmed" size="sm" py="md">
        Call details are not available.
      </Text>
    );
  }

  const isLive = !log && session?.status === 'active';
  const startedAt = log?.startedAt ?? session?.startedAt ?? null;
  const flow = log?.flow ?? session?.flow ?? null;
  const phoneNumber = log?.phoneNumber ?? session?.phoneNumber ?? null;
  const direction = log?.direction ?? session?.direction ?? 'inbound';
  const status = log?.status ?? session?.status ?? 'active';
  const collectedData = collectedEntries(log?.collectedData ?? session?.variables);
  const media = detail.recordings.length + detail.voicemails.length;
  const steps = detail.steps.map((step, index) => ({
    ...step,
    durationSeconds: Math.max(
      0,
      Math.round(
        (toMs(step.occurredAt) - toMs(detail.steps[index - 1]?.occurredAt ?? startedAt ?? step.occurredAt)) / 1000,
      ),
    ),
  }));

  return (
    <Stack gap="lg">
      <Group gap="xs">
        <Text fw={700}>{formatPhone(log?.fromNumber ?? session?.fromNumber ?? '')}</Text>
        <Text c="dimmed">→</Text>
        <Text fw={700}>
          {phoneNumber?.friendlyName ?? formatPhone(phoneNumber?.phoneNumber ?? log?.toNumber ?? '')}
        </Text>
        <Badge size="sm" variant="light" color={direction === 'inbound' ? 'blue' : 'grape'}>
          {direction}
        </Badge>
        <Badge size="sm" variant="filled" color={callStatusColor(status)}>
          {isLive ? 'live' : status}
        </Badge>
      </Group>

      <Group gap="xl">
        <div>
          <SectionLabel>Flow</SectionLabel>
          <Text size="sm">{flow?.name ?? '—'}</Text>
        </div>
        <div>
          <SectionLabel>Duration</SectionLabel>
          <Text size="sm">
            {isLive
              ? 'in progress'
              : formatDuration(log?.durationSeconds ?? (startedAt ? durationBetween(startedAt, session?.endedAt) : 0))}
          </Text>
        </div>
        <div>
          <SectionLabel>Started</SectionLabel>
          <Text size="sm">{startedAt ? formatStamp(startedAt) : '—'}</Text>
        </div>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="flow">Flow</Tabs.Tab>
          <Tabs.Tab value="variables">
            <Group gap={6}>
              Variables
              <TabCount count={collectedData.length} color="gray" />
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="errors">
            <Group gap={6}>
              Errors
              <TabCount count={detail.errors.length} color="red" />
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="media">
            <Group gap={6}>
              Media
              <TabCount count={media} color="gray" />
            </Group>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="flow" pt="md">
          {steps.length === 0 ? (
            <Text c="dimmed" size="sm">
              No steps recorded for this call.
            </Text>
          ) : (
            <ScrollArea.Autosize mah={420}>
              <Timeline active={steps.length} bulletSize={16} lineWidth={2}>
                {steps.map((step) => (
                  <Timeline.Item
                    key={step.id}
                    title={
                      <Group gap="xs">
                        <Text fw={600} size="sm">
                          {step.nodeName}
                        </Text>
                        <Badge size="xs" variant="light" color="gray">
                          {step.widgetType}
                        </Badge>
                        {step.handleTaken && (
                          <Badge size="xs" variant="outline" color="blue">
                            {step.handleTaken}
                          </Badge>
                        )}
                        <Badge size="xs" variant="light" color="teal">
                          {formatDuration(step.durationSeconds)}
                        </Badge>
                      </Group>
                    }
                  >
                    {step.input && (
                      <Text size="xs" c="dimmed">
                        Input: {step.input}
                      </Text>
                    )}
                    {step.output && (
                      <Text size="xs" c="dimmed">
                        Output: {step.output}
                      </Text>
                    )}
                    <Text size="xs" c="dimmed">
                      {formatStamp(step.occurredAt)}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            </ScrollArea.Autosize>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="variables" pt="md">
          {collectedData.length === 0 ? (
            <Text c="dimmed" size="sm">
              No variables collected for this call.
            </Text>
          ) : (
            <ScrollArea.Autosize mah={420}>
              <Table>
                <Table.Tbody>
                  {collectedData.map((entry) => (
                    <Table.Tr key={entry.key}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {entry.key}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{entry.value}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="errors" pt="md">
          {detail.errors.length === 0 ? (
            <Text c="dimmed" size="sm">
              No errors recorded for this call.
            </Text>
          ) : (
            <Stack gap="xs">
              {detail.errors.map((err) => (
                <Paper key={err.id} p="xs" radius="md" withBorder>
                  <Group gap="xs" mb={2}>
                    <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />
                    <Badge size="xs" color={err.severity === 'fatal' ? 'red' : 'yellow'} variant="filled">
                      {err.severity}
                    </Badge>
                    <Text size="sm" fw={600}>
                      {CALL_ERROR_TYPE_LABELS[err.errorType] ?? err.errorType}
                    </Text>
                    {err.nodeName && (
                      <Text size="xs" c="dimmed">
                        {err.nodeName}
                      </Text>
                    )}
                  </Group>
                  <Text size="xs">{err.message}</Text>
                  <CallErrorDetail detail={err.details} />
                </Paper>
              ))}
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="media" pt="md">
          {media === 0 ? (
            <Text c="dimmed" size="sm">
              No recordings or voicemails for this call.
            </Text>
          ) : (
            <Stack gap="md">
              {detail.recordings.length > 0 && (
                <Stack gap="xs">
                  <SectionLabel>Recordings</SectionLabel>
                  {detail.recordings.map((rec) => (
                    <Group key={rec.id} justify="space-between">
                      <Text size="sm">
                        {recordingFormat(rec)?.toUpperCase() ?? 'Recording'} ·{' '}
                        {formatDuration(durationBetween(rec.startedAt, rec.endedAt))}
                      </Text>
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconPlayerPlay size={14} />}
                        loading={loadingAudio === rec.id}
                        onClick={() => void playRecording(rec.id)}
                      >
                        Play
                      </Button>
                    </Group>
                  ))}
                </Stack>
              )}

              {detail.voicemails.length > 0 && (
                <Stack gap="xs">
                  <SectionLabel>Voicemails</SectionLabel>
                  {detail.voicemails.map((vm) => (
                    <Group key={vm.id} justify="space-between">
                      <Group gap="xs">
                        <Text size="sm">{formatDuration(vm.durationSeconds)}</Text>
                        <Badge size="xs" variant="light" color="gray">
                          {vm.status}
                        </Badge>
                      </Group>
                      {(vm.s3Key || vm.recordingUrl || vm.publicRecordingUrl) && (
                        <Button
                          size="compact-xs"
                          variant="light"
                          leftSection={<IconPlayerPlay size={14} />}
                          loading={loadingAudio === vm.id}
                          onClick={() => void playVoicemail(vm.id)}
                        >
                          Play
                        </Button>
                      )}
                    </Group>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>

      {audioError && (
        <Text size="sm" c="red">
          {audioError}
        </Text>
      )}
      {audioUrl && <audio key={audioUrl} src={audioUrl} controls autoPlay style={{ width: '100%' }} />}
    </Stack>
  );
}
